/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import WaterScheduleFields from '../WaterScheduleFields';
import { emptyScheduleEntry, summarizeScheduleEntry } from '../../../utils/waterSchedule';

/**
 * Irrigation section of a request form.
 *
 * The client picks every water source they need from the facility's catalog —
 * as many as they like — and each one gets its own two-week day pattern,
 * frequency, duration and notes.
 *
 * Waters are scoped to the compartment's facility: a compartment can only be
 * watered with what its own facility supplies, so offering the whole catalog
 * would invite requests the facility cannot fill. The facility comes from the
 * compartment the client selected at the top of the form, falling back to the
 * request's own facility when the form is being viewed or edited later.
 *
 * Nothing here is required. Watering was not tracked against a project before
 * this, so a request may legitimately say nothing about it, or say only half
 * of it.
 */
const WaterScheduleBuilder = ({ field, value, onChange, disabled, facilityId }) => {
  const [waters, setWaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entries = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = facilityId ? `/api/waters?facility=${facilityId}` : '/api/waters';
    axios.get(url)
      .then(({ data }) => { if (!cancelled) setWaters(Array.isArray(data) ? data : []); })
      .catch((err) => {
        console.error('Failed to load waters:', err);
        if (!cancelled) setError('Could not load the water list for this facility.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId]);

  const selectedIds = entries.map(e => String(e.water || '')).filter(Boolean);

  const labelFor = (id) => {
    const w = waters.find(x => String(x._id) === String(id));
    if (!w) return 'Water';
    return w.label || w.description || 'Water';
  };

  /**
   * Add or drop entries to match the selection. Existing entries are kept as
   * they are, so unticking a water by accident and re-ticking it does not
   * silently wipe the grid the client already filled in.
   */
  const handleSelectionChange = (event) => {
    const ids = event.target.value;
    const next = [];
    for (const id of ids) {
      const existing = entries.find(e => String(e.water || '') === String(id));
      next.push(existing || emptyScheduleEntry(id, labelFor(id)));
    }
    onChange(field.name, next);
  };

  const patchEntry = (index, updated) => {
    const next = entries.map((e, i) => (i === index ? updated : e));
    onChange(field.name, next);
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading water options…</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      {error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}

      {waters.length === 0 ? (
        <Alert severity="info">
          No water sources are set up for this facility yet, so there is nothing to schedule.
        </Alert>
      ) : (
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id="water-schedule-select-label">{field.label || 'Water Sources'}</InputLabel>
          <Select
            labelId="water-schedule-select-label"
            multiple
            value={selectedIds}
            onChange={handleSelectionChange}
            input={<OutlinedInput label={field.label || 'Water Sources'} />}
            renderValue={(ids) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ids.map(id => <Chip key={id} size="small" label={labelFor(id)} />)}
              </Box>
            )}
          >
            {waters.map(w => (
              <MenuItem key={w._id} value={String(w._id)}>
                <Checkbox checked={selectedIds.includes(String(w._id))} size="small" />
                <ListItemText
                  primary={w.label || w.description}
                  secondary={w.label && w.description !== w.label ? w.description : null}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {field.helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {field.helper}
        </Typography>
      )}

      {entries.length > 0 && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {entries.map((entry, index) => (
            <Paper key={entry.water || `entry-${index}`} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }} flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {entry.water_label || labelFor(entry.water)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summarizeScheduleEntry({ ...entry, water_label: '' })}
                </Typography>
              </Stack>
              <WaterScheduleFields
                entry={entry}
                disabled={disabled}
                onChange={(updated) => patchEntry(index, {
                  ...updated,
                  // Keep the name with the record: a historical schedule must
                  // still read correctly if the catalog entry is renamed.
                  water_label: updated.water_label || labelFor(entry.water)
                })}
              />
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default WaterScheduleBuilder;
